import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { css, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { Label } from '@components/Label';
import { Button } from '@components/index';
import { AttachmentFile, PDFPreviewModal } from '@pages/customer/common/file';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import { PencilIcon } from '@icons/PencilIcon';
import { TrashIcon } from '@icons/TrashIcon';
import type {
  SubscriptionRequestHistoryCreateDTO,
  SubscriptionRequestStatus,
} from '@repositories/subscriptionRequestRepository/subscriptionRequest.types';
import type { PdfFilePreview } from '@customHooks/useFileDialog';
import { useDownloadFile } from '@queryHooks/useFile';
import {
  useCreateSubscriptionRequestHistoryMutation,
  useDeleteSubscriptionRequest,
  useGetSubscriptionRequest,
} from '@queryHooks/useSubscriptionRequest';
import { download } from '@utils/fileUtil';
import type { SubscriptionRequestHistoryDTOModel } from '@repositories/subscriptionRequestRepository/models/SubscriptionRequestHistoryDTOModel';
import { HistoryRequestTable } from './HistoryRequestTable';
import { SubscriptionRequestHistoryProcessModal } from './SubscriptionRequestHistoryProcessModal';
import { SubscriptionRequestUpdateModal } from './SubscriptionRequestUpdateModal';

type GroupedHistoriesByStatus = Record<SubscriptionRequestStatus, SubscriptionRequestHistoryDTOModel[]>;

export const History = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const rqId = parseInt(id ?? '', 10);
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    categoryStore,
    authStore,
    uiStore: { alertStore, toastStore },
  } = useStore();

  const [scrollTarget, setScrollTarget] = useState<HTMLElement | null>(null);

  // 상세내역 수정 모달 오픈 여부
  const [isOpenSubscriptionRequestUpdateModal, setIsOpenSubscriptionRequestUpdateModal] = useState<boolean>(false);

  // 처리 내역 견적 검토 모달 오픈 여부
  const [isOpenSubscriptionRequestHistoryProcessModal, setIsOpenSubscriptionRequestHistoryProcessModal] =
    useState<boolean>(false);

  const [pdfPreview, setPdfPreview] = useState<PdfFilePreview>({
    open: false,
    file: null,
    fileName: '',
    fileId: null,
    numPages: 0,
  });

  const { data: subscriptionRequest, isLoading: isSubscriptionRequestLoading } = useGetSubscriptionRequest(rqId);
  const { mutate: deleteSubscriptionRequest } = useDeleteSubscriptionRequest();
  const { mutate: createSubscriptionRequestHistory } = useCreateSubscriptionRequestHistoryMutation();
  const { mutateAsync: downloadFile } = useDownloadFile();

  const rootCategory = subscriptionRequest?.software.category
    ? categoryStore.findRootCategory(subscriptionRequest.software.category.id)
    : undefined;

  const latestHistory = subscriptionRequest?.getHistories(false)[0];
  const lastestHistoryId = latestHistory?.id;
  const lastestHistoryStatus = latestHistory?.status;

  const histories = subscriptionRequest?.histories ?? [];

  // 수정, 삭제 불가 조건
  const disabledUpdateBtn = lastestHistoryStatus === 'DELIVERED';

  const findAttachment = (fileId: string) => {
    const historyAttachments = histories.flatMap(history => history.attachments);
    return historyAttachments.find(file => file?.fileId === fileId);
  };

  const handlePdfPreview = async (fileId: string) => {
    const attachment = findAttachment(fileId);

    if (!attachment) {
      toastStore.open({ message: t('File.Not_Found') });
      return;
    }

    if (attachment.fileExtension?.toLowerCase() !== 'pdf') {
      toastStore.open({ message: t('File.PDF_Only') });
      return;
    }

    try {
      const fileBlob = await downloadFile(fileId);
      setPdfPreview({
        open: true,
        file: fileBlob,
        fileName: `${attachment.fileName}.${attachment.fileExtension}`,
        fileId,
        numPages: 0,
      });
    } catch (error) {
      console.error(t('File.Load_Failed'), error);
      toastStore.open({ message: t('File.Load_Failed') });
    }
  };

  // 상세내역 수정 버튼 클릭
  const handleClickUpdateSubscriptionRequestBtn = () => {
    setIsOpenSubscriptionRequestUpdateModal(true);
  };

  // 처리내역 신청 처리 버튼 클릭
  const handleClickSubscriptionRequestHistoryProcessBtn = () => {
    setIsOpenSubscriptionRequestHistoryProcessModal(true);
  };

  // 상세내역 삭제 버튼 클릭
  const handleClickDeleteSubscriptionRequestBtn = () => {
    alertStore.open({
      title: t('History.Delete_Title'),
      message: t('Subscrib_Detail_BuyRequest_11'),
      useCancel: true,
      confirmName: t('History.Delete'),
      type: 'error',
      onConfirm: () => {
        deleteSubscriptionRequest(rqId);
        navigate(-1);
        toastStore.open(t('History.Delete_Success'));
      },
    });
  };

  // 구매 취소 버튼
  const handleClickPurchaseCancel = () => {
    alertStore.open({
      title: t('Purchase_History.Purchase_Cancel_Title'),
      message: t('Purchase_History.Purchase_Cancel_Message'),
      useCancel: true,
      type: 'error',
      onConfirm: () => {
        if (!lastestHistoryId) {
          toastStore.open(t('Purchase_History.Purchase_Cancel_Failed'));
          return;
        }

        const rqData: SubscriptionRequestHistoryCreateDTO = {
          subscriptionRequestId: rqId,
          managerId: authStore.id,
          status: 'CANCELLED',
          comment: '',
        };
        createSubscriptionRequestHistory(
          {
            ...rqData,
          },
          {
            onSuccess: () => {
              toastStore.open(t('Purchase_History.Purchase_Cancel_Complete'));
            },
            onError: () => {
              toastStore.open(t('Purchase_History.Purchase_Cancel_Failed'));
            },
          },
        );
      },
    });
  };

  // 파일 다운로드
  const handleDownloadFile = async (fileId: string) => {
    try {
      const historyAttachments = histories.flatMap(history => history.attachments);
      const attachment = historyAttachments.find(file => file?.fileId === fileId);
      if (!attachment) {
        toastStore.open({ message: t('File.Not_Found') });
        return;
      }
      const fileBlob = await downloadFile(fileId);
      const fileFullName = `${attachment.fileName}.${attachment.fileExtension}`;
      download(fileBlob, fileFullName);
    } catch (error) {
      console.error(t('File.Download_Failed'), error);
      toastStore.open({ message: t('File.Download_Failed') });
    }
  };

  // PDF 미리보기 변경
  const handleChangePdfPreview = (pdfPreview: PdfFilePreview) => {
    setPdfPreview(pdfPreview);
  };

  const groupHistoriesByStatus = (histories: SubscriptionRequestHistoryDTOModel[] | null): GroupedHistoriesByStatus => {
    const initialGroup: GroupedHistoriesByStatus = {
      APPROVED: [],
      CANCELLED: [],
      DELIVERED: [],
      QUOTATION: [],
      REJECTED: [],
      REQUESTED: [],
    };

    if (!histories) {
      return initialGroup;
    }

    return histories.reduce((acc, history) => {
      const { status } = history;
      if (status) {
        acc[status].push(history);
      }
      return acc;
    }, initialGroup);
  };

  const groupedHistories = groupHistoriesByStatus(histories);

  // 신청, 견적, 발주, 납품 상태별 첨부파일 추출
  const applyAttachments = groupedHistories.REQUESTED.flatMap(history => history.attachments);
  const quotationAttachments = groupedHistories.QUOTATION.flatMap(history => history.attachments);
  const approvedAttachments = groupedHistories.APPROVED.flatMap(history => history.attachments);
  const deliveredAttachments = groupedHistories.DELIVERED.flatMap(history => history.attachments);

  return (
    <Container ref={setScrollTarget}>
      <Inner>
        <div className="header">
          <strong>{t('Purchase_History.Title')}</strong>
          <IconButton onClick={() => navigate(-1)} startIcon={<ChevronIcon rotateNum={270} width={20} height={20} />}>
            {t('Purchase_History.List')}
          </IconButton>
        </div>
        <HistoryList>
          <HistoryItem>
            <TitleBox>
              <strong>{t('Purchase_History.Details')}</strong>
              {subscriptionRequest ? (
                <div>
                  <TitleButton
                    onClick={() => handleClickUpdateSubscriptionRequestBtn()}
                    startIcon={
                      <PencilIcon
                        width={16}
                        height={16}
                        color={disabledUpdateBtn ? theme.colors['ic-gray-light'] : '#24292E'}
                      />
                    }
                    disabled={disabledUpdateBtn}
                  >
                    {t('Ad_Cart_09')}
                  </TitleButton>
                  <TitleButton
                    onClick={() => handleClickDeleteSubscriptionRequestBtn()}
                    startIcon={
                      <TrashIcon
                        width={16}
                        height={16}
                        color={disabledUpdateBtn ? theme.colors['ic-gray-light'] : '#24292E'}
                      />
                    }
                    disabled={disabledUpdateBtn}
                  >
                    {t('Subscrib_Detail_BuyRequest_12')}
                  </TitleButton>
                </div>
              ) : null}
            </TitleBox>
            <SubBodyWrap>
              <SubItem>
                <SubItemTitle variant="headline9" component="strong">
                  {t('Subscrib_Detail_UseRequest_06')}
                </SubItemTitle>
                <Typography variant="headline8" component="p">
                  {rootCategory ? rootCategory.name : '-'}
                </Typography>
              </SubItem>
              <SubItem>
                <SubItemTitle variant="headline9" component="strong">
                  {t('Subscrib_Detail_UseRequest_25')}
                </SubItemTitle>
                <Typography variant="headline8" component="p">
                  {subscriptionRequest.tenant.name}
                </Typography>
              </SubItem>
              <SubItem>
                <SubItemTitle variant="headline9" component="strong">
                  {t('Subscrib_Detail_UseRequest_07')}
                </SubItemTitle>
                <Typography variant="headline8" component="p">
                  {subscriptionRequest.software.name}
                </Typography>
              </SubItem>
              <SubItem>
                <SubItemTitle variant="headline9" component="strong">
                  {t('Subscrib_Detail_BuyRequest_06')}
                </SubItemTitle>
                <Typography variant="headline8" component="p">
                  {dayjs(subscriptionRequest.formattedCreatedDate).format(t('DateFormat_YMD'))}
                </Typography>
              </SubItem>
              <SubItem>
                <SubItemTitle variant="headline9" component="strong">
                  {t('Subscrib_Main_12')}
                </SubItemTitle>
                <Typography variant="headline8" component="p">
                  {subscriptionRequest.productPlanName}
                </Typography>
              </SubItem>
              <SubItem>
                <SubItemTitle variant="headline9" component="strong">
                  {t('Subscrib_Detail_BuyRequest_02')}
                </SubItemTitle>
                <Typography variant="headline8" component="p">
                  {subscriptionRequest.user.email}
                </Typography>
              </SubItem>
              <SubItem $fullWidth>
                <SubItemTitle variant="headline9" component="strong">
                  {t('Purchase_History.Content')}
                </SubItemTitle>
                <Typography variant="headline8" component="p" sx={{ whiteSpace: 'pre-line' }}>
                  {subscriptionRequest?.inquiry || '-'}
                </Typography>
              </SubItem>
              <SubItem $fullWidth>
                <SubItemTitle variant="headline9" component="strong">
                  {t('Purchase_History.Attachment')}
                </SubItemTitle>
                <Typography variant="headline8" component="div">
                  <AttachmentFileWrapper>
                    {/* 신청 */}
                    {applyAttachments.length > 0 && (
                      <AttachmentSection>
                        <AttachmentSectionTitle>{t('Subscrib_Detail_BuyRequest_16')}</AttachmentSectionTitle>
                        {applyAttachments.map(attachment => (
                          <AttachmentFile
                            key={attachment.fileId}
                            attachment={{
                              fileId: attachment.fileId,
                              fileName: attachment.fileName,
                              fileExtension: attachment.fileExtension,
                              file: new File([], `${attachment.fileName}.${attachment.fileExtension}`),
                            }}
                            showPdfPreviewFn={handlePdfPreview}
                            downloadFileFn={handleDownloadFile}
                            enabledDeleteFile={false}
                            enabledDownload
                          />
                        ))}
                      </AttachmentSection>
                    )}

                    {/* 견적 */}
                    {quotationAttachments.length > 0 && (
                      <AttachmentSection>
                        <AttachmentSectionTitle>{t('Subscrib_Detail_BuyRequest_20')}</AttachmentSectionTitle>
                        {quotationAttachments.map(attachment => (
                          <AttachmentFile
                            key={attachment.fileId}
                            attachment={{
                              fileId: attachment.fileId,
                              fileName: attachment.fileName,
                              fileExtension: attachment.fileExtension,
                              file: new File([], `${attachment.fileName}.${attachment.fileExtension}`),
                            }}
                            showPdfPreviewFn={handlePdfPreview}
                            downloadFileFn={handleDownloadFile}
                            enabledDeleteFile={false}
                            enabledDownload
                          />
                        ))}
                      </AttachmentSection>
                    )}

                    {/* 발주 */}
                    {approvedAttachments.length > 0 && (
                      <AttachmentSection>
                        <AttachmentSectionTitle>{t('Subscrib_Detail_BuyRequest_18')}</AttachmentSectionTitle>
                        {approvedAttachments.map(attachment => (
                          <AttachmentFile
                            key={attachment.fileId}
                            attachment={{
                              fileId: attachment.fileId,
                              fileName: attachment.fileName,
                              fileExtension: attachment.fileExtension,
                              file: new File([], `${attachment.fileName}.${attachment.fileExtension}`),
                            }}
                            showPdfPreviewFn={handlePdfPreview}
                            downloadFileFn={handleDownloadFile}
                            enabledDeleteFile={false}
                            enabledDownload
                          />
                        ))}
                      </AttachmentSection>
                    )}

                    {/* 납품 */}
                    {deliveredAttachments.length > 0 && (
                      <AttachmentSection>
                        <AttachmentSectionTitle>{t('Subscrib_Detail_BuyRequest_17')}</AttachmentSectionTitle>
                        {deliveredAttachments.map(attachment => (
                          <AttachmentFile
                            key={attachment.fileId}
                            attachment={{
                              fileId: attachment.fileId,
                              fileName: attachment.fileName,
                              fileExtension: attachment.fileExtension,
                              file: new File([], `${attachment.fileName}.${attachment.fileExtension}`),
                            }}
                            showPdfPreviewFn={handlePdfPreview}
                            downloadFileFn={handleDownloadFile}
                            enabledDeleteFile={false}
                            enabledDownload
                          />
                        ))}
                      </AttachmentSection>
                    )}
                  </AttachmentFileWrapper>
                </Typography>
              </SubItem>
            </SubBodyWrap>
          </HistoryItem>
          {/* 첫 번째 이력 상세 정보 */}

          {latestHistory && (
            <HistoryItem>
              <Typography variant="headline7" component="strong">
                {t('Request_Processing')}
              </Typography>
              <SubBodyWrap $columns={4}>
                <SubItem $nonBorder>
                  <SubItemTitle variant="headline9" component="strong">
                    {t('Subscrib_Detail_UseRequest_09')}
                  </SubItemTitle>
                  <div className="label-box">
                    <Label variant="contain" color={subscriptionRequest.getStatusLabelColor(latestHistory.status)}>
                      {subscriptionRequest.getStatusStr(latestHistory.status)}
                    </Label>
                  </div>
                </SubItem>
                <SubItem $nonBorder>
                  <SubItemTitle variant="headline9" component="strong">
                    {t('Acc_Detail_12')}
                  </SubItemTitle>
                  <Typography variant="headline8" component="p">
                    {latestHistory.managerName || '-'}
                  </Typography>
                </SubItem>
                <SubItem $columns={4} $nonBorder>
                  <SubItemTitle variant="headline9" component="strong">
                    {t('Subscrib_Detail_UseRequest_19')}
                  </SubItemTitle>
                  <Typography variant="headline8" component="p">
                    {dayjs(latestHistory.createdDate).format(t('DateFormat_YMD'))}
                  </Typography>
                </SubItem>
                <SubItem>
                  <HistoryButtonContainer>
                    <HistoryButton
                      variant="outline"
                      size="small"
                      onClick={handleClickPurchaseCancel}
                      disabled={lastestHistoryStatus === 'DELIVERED' || lastestHistoryStatus === 'CANCELLED'}
                    >
                      {t('Purchase_History.Purchase_Cancel')}
                    </HistoryButton>
                    <HistoryButton
                      variant="contain"
                      size="small"
                      onClick={handleClickSubscriptionRequestHistoryProcessBtn}
                      disabled={
                        lastestHistoryStatus === 'DELIVERED' ||
                        lastestHistoryStatus === 'REQUESTED' ||
                        lastestHistoryStatus === 'APPROVED' ||
                        lastestHistoryStatus === 'CANCELLED'
                      }
                    >
                      {t('Purchase_History.Quotation_Review')}
                    </HistoryButton>
                  </HistoryButtonContainer>
                </SubItem>
              </SubBodyWrap>
            </HistoryItem>
          )}
        </HistoryList>
        {/* 전체 이력 테이블 */}
        <HistoryTableWrapper ref={setScrollTarget}>
          <HistoryRequestTable
            subscriptionRequest={subscriptionRequest}
            isSubscriptionRequestLoading={isSubscriptionRequestLoading}
            scrollTarget={scrollTarget}
          />
        </HistoryTableWrapper>

        {isOpenSubscriptionRequestUpdateModal && (
          <SubscriptionRequestUpdateModal
            isOpen={isOpenSubscriptionRequestUpdateModal}
            onClose={() => setIsOpenSubscriptionRequestUpdateModal(false)}
            subscriptionRequestData={subscriptionRequest}
          />
        )}

        {/* 처리 내역 견적 검토 모달 */}
        {isOpenSubscriptionRequestHistoryProcessModal && (
          <SubscriptionRequestHistoryProcessModal
            isOpen={isOpenSubscriptionRequestHistoryProcessModal}
            latestHistory={latestHistory}
            onClose={() => setIsOpenSubscriptionRequestHistoryProcessModal(false)}
            showPdfPreviewFn={handlePdfPreview}
            downloadFileFn={handleDownloadFile}
          />
        )}
        {pdfPreview.open && (
          <PDFPreviewModal
            pdfPreview={pdfPreview}
            updatePdfPreview={handleChangePdfPreview}
            downloadFileFn={handleDownloadFile}
            enabledDownload
          />
        )}
      </Inner>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: calc(100vh - var(--manager-gnb-height) - var(--employee-sub-gnb-height));
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px 32px 32px;
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  & .header {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 60px;
    align-items: center;
    margin: 60px auto 43px;
    & strong {
      ${fonts.Headline4};
      color: ${({ theme }) => theme.colors['text-gray-main']};
    }
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const HistoryList = styled.ul`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const HistoryItem = styled.li`
  width: 100%;
`;

const SubBodyWrap = styled.ul<{ $columns?: number }>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns ?? 3}, 1fr);
  margin-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.02), 0px 0px 3px 0px rgba(0, 0, 0, 0.07);
  border-radius: 5px;
  background-color: ${({ theme }) => theme.colors['bg-white']};
`;

const SubItem = styled.li<{
  $fullWidth?: boolean;
  $columns?: number;
  $nonBorder?: boolean;
}>`
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      grid-column: 1 / -1;
    `}
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 15px 20px;
  border-bottom: ${({ $nonBorder, theme }) =>
    $nonBorder ? 'none' : `1px solid ${theme.colors['border-gray-w-lighter']}`};
  border-right: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};

  /* Grid의 각 행 마지막 컬럼은 border-right 제거 */
  &:nth-of-type(${({ $columns }) => $columns ?? 3}n) {
    border-right: none;
  }

  &:last-of-type {
    border: none;
  }

  & .label-box {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    height: 18px;
  }
`;

const SubItemTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
` as typeof Typography;

const TitleBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  & strong {
    ${fonts.Headline7};
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
`;

const IconButton = styled(Button)`
  gap: 4px;
  margin-right: auto;
  & span {
    ${fonts.Headline7};
  }
`;

const TitleButton = styled(Button)`
  display: inline-flex;
  height: 32px;
  padding: 0 12px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  width: auto;
  margin-left: 8px;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  background-color: ${({ theme }) => theme.colors['bg-white']};
  & span {
    ${fonts.Button4};
  }
`;

const AttachmentFileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AttachmentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AttachmentSectionTitle = styled.div`
  ${fonts.Headline9};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  font-weight: 600;
`;

const HistoryButton = styled(Button)`
  display: inline-flex;
  height: 32px;
  padding: 6px 16px;
  justify-content: center;
  align-items: center;
  gap: 2px;
`;

const HistoryButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  height: 100%;
  justify-content: flex-end;
  align-items: center;
`;

const HistoryTableWrapper = styled.div``;
